import { useEffect, useMemo, useRef, useState } from 'react';
import { io } from 'socket.io-client';

const DEFAULT_SOCKET_URL = process.env.REACT_APP_BADGE_SOCKET_URL || 'http://127.0.0.1:5000';

const normalizeUid = (value = '') =>
  String(value)
    .trim()
    .toUpperCase()
    .replace(/^0X/, '')
    .replace(/[^A-Z0-9]/g, '');

const parsePayload = (payload) => {
  if (!payload) return '';

  if (typeof payload === 'string') {
    try {
      const parsed = JSON.parse(payload);
      return normalizeUid(parsed?.uid || parsed?.cardUid || parsed?.id || '');
    } catch {
      return normalizeUid(payload);
    }
  }

  if (typeof payload === 'object') {
    return normalizeUid(payload?.uid || payload?.cardUid || payload?.id || '');
  }

  return '';
};

export const useLocalBadgeSocket = () => {
  const [connected, setConnected] = useState(false);
  const [lastScan, setLastScan] = useState(null);
  const [socketError, setSocketError] = useState('');
  const seqRef = useRef(0);

  const socketUrl = useMemo(() => DEFAULT_SOCKET_URL, []);

  useEffect(() => {
    const socket = io(socketUrl, {
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionAttempts: Infinity,
      reconnectionDelay: 1000,
      timeout: 10000,
    });

    const onConnect = () => {
      setConnected(true);
      setSocketError('');
    };

    const onDisconnect = () => {
      setConnected(false);
    };

    const onConnectError = (err) => {
      setConnected(false);
      setSocketError(err?.message || 'Bridge local indisponible');
    };

    const onAny = (eventName, payload) => {
      const uid = parsePayload(payload);
      if (!uid) return;

      seqRef.current += 1;
      setLastScan({
        uid,
        seq: seqRef.current,
        at: Date.now(),
        eventName,
      });
    };

    socket.on('connect', onConnect);
    socket.on('disconnect', onDisconnect);
    socket.on('connect_error', onConnectError);
    socket.onAny(onAny);

    return () => {
      socket.off('connect', onConnect);
      socket.off('disconnect', onDisconnect);
      socket.off('connect_error', onConnectError);
      socket.offAny(onAny);
      socket.close();
    };
  }, [socketUrl]);

  return {
    connected,
    lastScan,
    socketError,
    socketUrl,
  };
};
